import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from 'helpers/api_helper';
import notistack from 'modules/core/notistack';

// Types for API responses
export interface ProductSearchResult {
  id: string;
  styleNo: string;
  category: string;
  subcategory: string;
  bagNo: string;
  igiNo: string;
  huidNo: string;
  netWt: number;
  grossWt: number;
  kt: number;
  diaGoldWt: number;
  diaQty: number;
  diaClarity: string;
  diaColor: string;
  pcs: number;
  mrp: number;
  tag: string;
  isSNMCC: boolean;
  color: string;
  metalColor: string;
  image?: string;
  isAvailable: boolean;
  stockQuantity: number;
}

export interface CustomerSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  mobileNo: string;
  email: string;
  address: string;
  cityId: number;
  stateId: number;
  countryId: number;
  zipCode: string;
  gstNo: string;
  panCardNo: string;
  birthdayDate: string;
  anniversaryDate: string;
  engagementDate: string;
  partnerName: string;
  partnerBirthdayDate: string;
  partnerContactNo: string;
  country: string;
  state: string;
  gender: string;
  city: string;
  creditAmount?: number;
  rewardPoints?: number;
}

export interface SalesPersonResult {
  id: string;
  firstName: string;
  lastName: string;
  employeeCode: string;
  department: string;
  isActive: boolean;
  commissionRate: number;
}

export interface CouponResult {
  id: string;
  code: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  minimumAmount: number;
  maximumDiscount: number;
  validFrom: string;
  validTo: string;
  isActive: boolean;
  usageLimit: number;
  usedCount: number;
}

export interface DiscountResult {
  id: string;
  name: string;
  discountType: 'percentage' | 'fixed';
  value: number;
  applicableCategories: string[];
  minimumAmount: number;
  maximumDiscount: number;
  isActive: boolean;
}

export interface PaymentTypeResult {
  id: string;
  name: string;
  type: 'cash' | 'card' | 'upi' | 'netbanking' | 'cheque' | 'other';
  isActive: boolean;
  chargePercentage: number;
  chargeFixed: number;
}

export const useProductDropdown = () => {
  const [allProductIds, setAllProductIds] = useState<Record<string, unknown[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Optimized loader with proper error handling
  const loadAllProductIds = useCallback(async () => {
    if (loading) return; // Prevent concurrent requests

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/v1/ProductDetails/GetAllProductIdsList');
      setAllProductIds(response.data || {});
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to load product IDs';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Auto-load on mount only with proper cleanup
  useEffect(() => {
    let mounted = true;
    let abortController = new AbortController();

    const load = async () => {
      if (!mounted) return;

      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get('/api/v1/ProductDetails/GetAllProductIdsList', {
          signal: abortController.signal
        });
        if (mounted) {
          setAllProductIds(response.data || {});
        }
      } catch (err: unknown) {
        if (mounted && (err as Error)?.name !== 'AbortError') {
          const error = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
          const errorMessage = error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to load product IDs';
          setError(errorMessage);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
      abortController.abort();
    };
  }, []); // Empty dependency array - load once

  return {
    allProductIds,
    loading,
    error,
    refresh: loadAllProductIds
  };
};

// Custom hook for product search
export const useProductSearch = () => {
  const [products, setProducts] = useState<ProductSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchType, setSearchType] = useState<'bagNo' | 'styleNo' | 'igiNo' | 'huid'>('bagNo');
  const abortControllerRef = useRef<AbortController>();

  const searchProducts = useCallback(async (searchTerm: string, type: 'bagNo' | 'styleNo' | 'igiNo' | 'huid' = 'bagNo') => {
    if (!searchTerm.trim()) {
      setProducts([]);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);
    setSearchType(type);

    try {
      // Then get detailed product information for the selected ID
      const productDetailsResponse = await apiClient.get(`/api/v1/SalesEntry/GetProductDetailsForSalesEntry?SearchId=${searchTerm}&ProductSearchBy=${type}`, {
        signal: abortControllerRef.current.signal
      });

      const productDetails = productDetailsResponse.data || [];

      // Transform the response to include additional metadata
      const transformedProducts = productDetails.map((product: any) => ({
        ...product,
        searchType,
        displayText: getDisplayText(product, type),
        secondaryText: getSecondaryText(product)
      }));

      setProducts(transformedProducts);
    } catch (err: any) {
      if ((err as Error)?.name !== 'AbortError') {
        const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to search products';
        setError(errorMessage);
        setProducts([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper functions for display text
  const getDisplayText = (product: ProductSearchResult, type: 'bagNo' | 'styleNo' | 'igiNo' | 'huid') => {
    switch (type) {
      case 'bagNo':
        return `${product.bagNo} - ${product.styleNo}`;
      case 'styleNo':
        return `${product.styleNo} - ${product.category}`;
      case 'igiNo':
        return `${product.igiNo} - ${product.styleNo}`;
      case 'huid':
        return `${product.huidNo} - ${product.styleNo}`;
      default:
        return product.styleNo;
    }
  };

  const getSecondaryText = (product: ProductSearchResult) => {
    return `${product.category} | ${product.kt}K | ${product.netWt}g`;
  };

  const getProductByBagNo = useCallback(async (bagNo: string) => {
    if (!bagNo.trim()) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/api/v1/Sales/GetProductByBagNo/${bagNo}`);
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to get product by bag number';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getProductByStyleNo = useCallback(async (styleNo: string) => {
    if (!styleNo.trim()) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/api/v1/Sales/GetProductByStyleNo/${styleNo}`);
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to get product by style number';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getProductByIGI = useCallback(async (igiNo: string) => {
    if (!igiNo.trim()) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/api/v1/Sales/GetProductByIGI/${igiNo}`);
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to get product by IGI number';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getProductByHUID = useCallback(async (huid: string) => {
    if (!huid.trim()) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/api/v1/Sales/GetProductByHUID/${huid}`);
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to get product by HUID';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getAllProductIds = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/v1/ProductDetails/GetAllProductIdsList');
      return response.data || [];
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to get product identifiers';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    products,
    loading,
    error,
    searchType,
    searchProducts,
    getProductByBagNo,
    getProductByStyleNo,
    getProductByIGI,
    getProductByHUID,
    getAllProductIds
  };
};

// Custom hook for customer search
export const useCustomerSearch = () => {
  const [customers, setCustomers] = useState<CustomerSearchResult[]>([]);
  const [currentCustomerNewForm, setCurrentCustomerNewForm] = useState([])
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchCustomers = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setCustomers([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use query string format as shown in the API endpoint
      const response = await apiClient.get(`/api/v1/Customer/GetAllCustomerGrid?Search=${encodeURIComponent(searchTerm)}`);
      console.log('response.data', response.data)
      setCustomers(response.data || []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to search customers';
      setError(errorMessage);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const getAllCustomers = useCallback(async (page: number = 1, limit: number = 50) => {
    setLoading(true);
    setError(null);

    try {
      // Use query string format for consistency with search
      const response = await apiClient.get(`/api/v1/Customer/GetAllCustomerGrid?page=${page}&limit=${limit}`);
      setCustomers(response.data || []);
      return response;
    } catch (err: any) {
      setError(err.message || 'Failed to get customers');
      setCustomers([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createCustomer = useCallback(async (customerData: Partial<CustomerSearchResult>) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/v1/Customer/CreateCustomer', customerData);
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to create customer';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  const getCustomerByMobileNo = useCallback(async (mobileNo: string) => {
    if (!mobileNo.trim()) return null;

    setLoading(true);
    setError(null);

    try {

      const response = await apiClient.get(`/api/v1/Customer/GetCustomerByMobileNo?mobileNo=${encodeURIComponent(mobileNo)}`);
      setCurrentCustomerNewForm(response.data)
      return response.data;
    } catch (err: any) {
      if (err.response?.status === 404) {
        return null;
      }
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to check customer by mobile number';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  const updateCustomer = useCallback(async (customerId: string, customerData: Partial<CustomerSearchResult>) => {
    setLoading(true);
    setError(null);

    try {
      // Include the customer ID in the payload for update operations
      const updatePayload = { ...customerData, id: customerId };
      const response = await apiClient.post(`/api/v1/Customer/UpdateCustomer`, updatePayload);


      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to update customer';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);



  const insertCustomer = useCallback(async (customerData: Partial<CustomerSearchResult>) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/v1/Customer/InsertCustomer', customerData);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to insert customer';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    customers,
    currentCustomerNewForm,
    setCurrentCustomerNewForm,
    loading,
    error,
    searchCustomers,
    getAllCustomers,
    createCustomer,
    updateCustomer,
    getCustomerByMobileNo,
    insertCustomer

  };
};

// Custom hook for sales person search
export const useSalesPersonSearch = () => {
  const [salesPersons, setSalesPersons] = useState<SalesPersonResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getSalesPersonSelectList = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/v1/sales/GetSalesPersonSelectList');
      const rawData = response.data || [];

      // Transform data to ensure consistent structure
      const transformedData = rawData.map((item: any) => ({
        ...item,
        // Ensure we have consistent field names
        value: item.value || item.id,
        text: item.text || item.name || `${item.firstName || ''} ${item.lastName || ''}`.trim() || '[No Name]',
        id: item.id || item.value,
        name: item.name || item.text || `${item.firstName || ''} ${item.lastName || ''}`.trim() || '[No Name]',
        // Preserve original fields
        firstName: item.firstName,
        lastName: item.lastName,
        employeeCode: item.employeeCode,
        department: item.department,
        isActive: item.isActive,
        disabled: item.disabled || !item.isActive,
        selected: item.selected || false
      }));

      setSalesPersons(transformedData);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to get sales person select list';
      setError(errorMessage);
      setSalesPersons([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const searchSalesPersons = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      // If search term is empty, get all sales persons instead of clearing
      getSalesPersonSelectList();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/v1/SalesPerson/SearchSalesPersons', {
        params: { search: searchTerm }
      });
      const rawData = response.data || [];

      // Transform data to ensure consistent structure
      const transformedData = rawData.map((item: any) => ({
        ...item,
        // Ensure we have consistent field names
        value: item.value || item.id,
        text: item.text || item.name || `${item.firstName || ''} ${item.lastName || ''}`.trim() || '[No Name]',
        id: item.id || item.value,
        name: item.name || item.text || `${item.firstName || ''} ${item.lastName || ''}`.trim() || '[No Name]',
        // Preserve original fields
        firstName: item.firstName,
        lastName: item.lastName,
        employeeCode: item.employeeCode,
        department: item.department,
        isActive: item.isActive,
        disabled: item.disabled || !item.isActive,
        selected: item.selected || false
      }));

      setSalesPersons(transformedData);
    } catch (err: any) {
      setError(err.message || 'Failed to search sales persons');
      setSalesPersons([]);
    } finally {
      setLoading(false);
    }
  }, [getSalesPersonSelectList]);

  const getAllSalesPersons = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { isActive: true };
      const response = await apiClient.get('/api/v1/SalesPerson/GetAllSalesPersons', params);
      const rawData = response.data || [];

      // Transform data to ensure consistent structure
      const transformedData = rawData.map((item: any) => ({
        ...item,
        // Ensure we have consistent field names
        value: item.value || item.id,
        text: item.text || item.name || `${item.firstName || ''} ${item.lastName || ''}`.trim() || '[No Name]',
        id: item.id || item.value,
        name: item.name || item.text || `${item.firstName || ''} ${item.lastName || ''}`.trim() || '[No Name]',
        // Preserve original fields
        firstName: item.firstName,
        lastName: item.lastName,
        employeeCode: item.employeeCode,
        department: item.department,
        isActive: item.isActive,
        disabled: item.disabled || !item.isActive,
        selected: item.selected || false
      }));

      setSalesPersons(transformedData);
      return response;
    } catch (err: any) {
      setError(err.message || 'Failed to get sales persons');
      setSalesPersons([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    salesPersons,
    loading,
    error,
    searchSalesPersons,
    getAllSalesPersons,
    getSalesPersonSelectList
  };
};

// Custom hook for coupon search
export const useCouponSearch = () => {
  const [coupons, setCoupons] = useState<CouponResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchCoupons = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setCoupons([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        search: searchTerm,
        isActive: true,
        page: 1,
        limit: 50
      };

      const response = await apiClient.get('/api/v1/Coupon/SearchCoupons', params);
      setCoupons(response.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to search coupons');
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const validateCoupon = useCallback(async (couponCode: string, totalAmount: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/v1/Coupon/ValidateCoupon', {
        couponCode,
        totalAmount
      });
      return response.data;
    } catch (err: any) {
      setError(err.message || 'Failed to validate coupon');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    coupons,
    loading,
    error,
    searchCoupons,
    validateCoupon
  };
};

// Custom hook for discount search
export const useDiscountSearch = () => {
  const [discounts, setDiscounts] = useState<DiscountResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAvailableDiscounts = useCallback(async (category?: string, totalAmount?: number) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        category,
        totalAmount,
        isActive: true
      };

      const response = await apiClient.get('/api/v1/Discount/GetAvailableDiscounts', params);
      setDiscounts(response.data || []);
      return response;
    } catch (err: any) {
      setError(err.message || 'Failed to get available discounts');
      setDiscounts([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    discounts,
    loading,
    error,
    getAvailableDiscounts
  };
};

// Custom hook for payment types
export const usePaymentTypes = () => {
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController>();

  const getPaymentTypes = useCallback(async () => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/v1/PaymentType/GetAllPaymentTypes', {
        signal: abortControllerRef.current.signal
      });
      setPaymentTypes(response.data || []);
      return response;
    } catch (err: any) {
      if ((err as Error)?.name !== 'AbortError') {
        setError(err.message || 'Failed to get payment types');
        setPaymentTypes([]);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load payment types on mount
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (mounted) {
        await getPaymentTypes();
      }
    };

    load();

    return () => {
      mounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []); // Empty dependency array

  return {
    paymentTypes,
    loading,
    error,
    getPaymentTypes
  };
};

// Custom hook for search suggestions
export const useSearchSuggestions = () => {
  const [searchData, setSearchData] = useState<{
    bagNos: string[];
    styleNos: string[];
    igiNos: string[];
    huidNos: string[];
  }>({
    bagNos: [],
    styleNos: [],
    igiNos: [],
    huidNos: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSearchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/v1/ProductDetails/GetAllProductIdsList');
      const productIds = response.data || [];


      setSearchData(productIds);
      return productIds;
    } catch (err: any) {
      setError(err.message || 'Failed to load search suggestions');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load on mount with proper cleanup
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!mounted) return;

      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get('/api/v1/ProductDetails/GetAllProductIdsList');
        if (mounted) {
          const productIds = response.data || [];
          setSearchData(productIds);
        }
      } catch (err: any) {
        if (mounted) {
          setError(err.message || 'Failed to load search suggestions');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => { mounted = false; };
  }, []); // Empty dependency array

  return {
    searchData,
    loading,
    error,
    loadSearchData
  };
};

// Custom hook for location (Country, State, City) search
export const useLocationSearch = () => {
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [loading, setLoading] = useState({
    countries: false,
    states: false,
    cities: false,
  });
  const [error, setError] = useState<string | null>(null);

  // const getCountries = useCallback(async () => {
  //   setLoading((prev) => ({ ...prev, countries: true }));
  //   setError(null);

  //   try {
  //     const response = await apiClient.get('/api/v1/Country/GetCountrySelectList');
  //     const countryData = response.data || [];
  //     setCountries(countryData);
  //     return response;
  //   } catch (err: any) {
  //     setError(err.message || 'Failed to get countries');
  //     setCountries([]);
  //     return null;
  //   } finally {
  //     setLoading((prev) => ({ ...prev, countries: false }));
  //   }
  // }, []);

  // const getStatesByCountryId = useCallback(async (countryId: string) => {
  //   if (!countryId) {
  //     setStates([]);
  //     return;
  //   }

  //   setLoading((prev) => ({ ...prev, states: true }));
  //   setError(null);

  //   try {
  //     const response = await apiClient.get(`/api/v1/StateProvince/GetStateProvinceSelectListByCountryId?countryId=${countryId}`);
  //     const stateData = response.data || [];
  //     setStates(stateData);
  //     return response;
  //   } catch (err: any) {
  //     setError(err.message || 'Failed to get states');
  //     setStates([]);
  //     return null;
  //   } finally {
  //     setLoading((prev) => ({ ...prev, states: false }));
  //   }
  // }, []);

  // const getCitiesByStateId = useCallback(async (stateId: string) => {
  //   if (!stateId) {
  //     setCities([]);
  //     return;
  //   }

  //   setLoading((prev) => ({ ...prev, cities: true }));
  //   setError(null);

  //   try {
  //     const response = await apiClient.get(`/api/v1/City/GetCitySelectListByStateId?stateId=${stateId}`);
  //     const cityData = response.data || [];
  //     setCities(cityData);
  //     return response;
  //   } catch (err: any) {
  //     setError(err.message || 'Failed to get cities');
  //     setCities([]);
  //     return null;
  //   } finally {
  //     setLoading((prev) => ({ ...prev, cities: false }));
  //   }
  // }, []);

  return {
    countries,
    states,
    cities,
    loading,
    error
  };
};

// Custom hook for bag number search
export const useBagNoSearch = () => {
  const [products, setProducts] = useState<ProductSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allProductIds, setAllProductIds] = useState<any[]>([]);

  // console.log('yogesh', products)

  // Load all product IDs on component mount for fast searching
  const loadAllProductIds = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/v1/ProductDetails/GetAllProductIdsList');
      const productIds = response.data || [];
      setAllProductIds(productIds);
    } catch (err: any) {
      console.error('Failed to load product IDs:', err);
    }
  }, []);

  // Auto-load product IDs on mount with proper cleanup
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!mounted) return;

      try {
        const response = await apiClient.get('/api/v1/ProductDetails/GetAllProductIdsList');
        if (mounted) {
          const productIds = response.data || [];
          setAllProductIds(productIds);
        }
      } catch (err: any) {
        console.log("Failed to load product IDs:", err);
      }
    };

    load();

    return () => { mounted = false; };
  }, []); // Empty dependency array

  const searchByBagNo = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setProducts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Filter product IDs based on bag number search
      let filteredIds = allProductIds.filter((product: any) => {
        const searchValue = searchTerm.toLowerCase();
        return product.bagNo?.toLowerCase().includes(searchValue);
      });

      // Limit results to prevent performance issues
      filteredIds = filteredIds.slice(0, 20);

      if (filteredIds.length === 0) {
        setProducts([]);
        return;
      }

      // Get detailed information for filtered products
      const detailedProducts = await Promise.all(
        filteredIds.map(async (product: any) => {
          try {
            const response = await apiClient.get('/api/v1/SalesEntry/GetProductDetailsForSalesEntry', {
              params: {
                SearchId: product.bagNo,
                ProductSearchBy: 'bagNo'
              }
            });

            const productDetails = response.data?.[0];
            if (productDetails) {
              return {
                ...productDetails,
                isAvailable: productDetails.isAvailable ?? true,
                stockQuantity: productDetails.stockQuantity ?? 1,
                image: productDetails.image || null,
                isSNMCC: productDetails.isSNMCC ?? false,
                diaQty: productDetails.diaQty ?? 0,
                diaClarity: productDetails.diaClarity || '',
                diaColor: productDetails.diaColor || '',
                diaGoldWt: productDetails.diaGoldWt ?? 0,
                color: productDetails.color || '',
                metalColor: productDetails.metalColor || '',
                pcs: productDetails.pcs ?? 1
              };
            }
            return null;
          } catch (err) {
            return null;
          }
        })
      );

      // Filter out null results and set products
      const validProducts = detailedProducts.filter(product => product !== null);
      setProducts(validProducts);
    } catch (err: any) {
      setError(err.message || 'Failed to search by bag number');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [allProductIds]);

  const getProductByBagNo = useCallback(async (bagNo: string) => {
    if (!bagNo.trim()) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/v1/SalesEntry/GetProductDetailsForSalesEntry', {
        params: {
          SearchId: bagNo,
          ProductSearchBy: 'bagNo'
        }
      });

      const productDetails = response.data?.[0];
      if (productDetails) {
        return {
          ...productDetails,
          isAvailable: productDetails.isAvailable ?? true,
          stockQuantity: productDetails.stockQuantity ?? 1,
          image: productDetails.image || null,
          isSNMCC: productDetails.isSNMCC ?? false,
        };
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Failed to get product by bag number');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    products,
    loading,
    error,
    searchByBagNo,
    getProductByBagNo,
    loadAllProductIds
  };
};

export interface CreateSalesEntryPayload {
  salesOrderDetailsModel: {
    salesOrderItemDetailsModelsList: Array<{
      mrp: number;
      subMrp: number;
      cgst: number;
      sgst: number;
      igst: number;
      isSnmcc: boolean;
      finalPrice: number;
      netPrice: number;
      goldRate: number;
      diaPrice: number;
      metalPrice: number;
      materialDiscount: number;
      diamondDiscount: number;
      labDiscount: number;
      mrpDiscount: number;
      productHeaderId: number;
      labMrp: number;
      deleted: boolean;
    }>;
    metalAmount: number;
    labourAmount: number;
    otherCharges: number;
    discountAmount: number;
    totalSoAmount: number;
    finalSoAmount: number;
    returnAmount: number;
    finalDueAmount: number;
    materialDiscount: number;
    diamondDiscount: number;
    labDiscount: number;
    mrpDiscount: number;
    customerId: number;
    salesPersonId: number;
    discountVoucherCodeId: number;
    discountVoucherAmount: number;
    storeDiscountOffer: number;
    cgst: number;
    sgst: number;
    subAmount: number;
    isOnGst: boolean;
    vocAmount: number;
    customerMobileNo: string;
  };
  paymentDetailsModelList: Array<{
    paymentType: string;
    amount: number;
    refNo: string;
  }>;
  discountVoucherDetails: {
    discountVoucherCodeId: number;
    discountVoucherAmount: number;
  };
  returnSalesOrderResponseModel?: {
    returnSalesOrderModel: {
      salesOrderId: number;
      returnSaleOrderDate: string;
      totalReturnSaleOrderAmount: number;
      totalQuantity: number;
      returnSaleType: string;
      customerId: number;
      storeId: number;
      salesPersonId: number;
      paymentType: string;
      paymentReferenceNumber: string;
      description: string;
    };
    returnSalesOrderItems: Array<{
      returnSalesOrderId: number;
      styleNo: number;
      categoryName: string;
      diamondWeight: number;
      netWeight: number;
      actualNetWeight: number;
      grossWeight: number;
      metalPrice: number;
      laborPrice: number;
      diamondQuantity: number;
      quantity: number;
      saleOrderAmount: number;
      returnSaleOrderAmount: number;
      materialTypeId: number;
      materialTypeName: string;
      productHeaderId: number;
      customerId: number;
    }>;
  };
  rewardRedeemDetailsModelsList?: Array<{
    materialTypeId: number;
    usedPoints: number;
    amountConverted: number;
    totalAmt: number;
    rewardPercentage: number;
  }>;
  purchaseOrderDetailsModel?: {
    purchaseOrderItemsDetails: Array<{
      karatId: number;
      rate: number;
      purity: number;
      netWeight: number;
      equivalentWeight: number;
      purchaseAmount: number;
      note: string;
    }>;
    totalAmount: number;
  };
}

// Custom hook for sales entry submission
export const useSalesEntrySubmission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createSalesEntry = useCallback(async (salesData: CreateSalesEntryPayload) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/v1/SalesEntry/CreateSalesEntry', salesData);
      return response;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.title ||
        err.message ||
        'Failed to create sales entry';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const calculateTotals = useCallback(async (items: any[], discounts: any[], coupons: any[]) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/v1/Sales/CalculateTotals', {
        items,
        discounts,
        coupons
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to calculate totals';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    createSalesEntry,
    calculateTotals
  };
};

// Custom hook for product details search by ID
export const useProductDetailsSearch = () => {
  const [productDetails, setProductDetails] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getProductDetailsForSalesEntry = useCallback(async (searchId: string, productSearchBy: 0 | 1 | 2 | 3, customeOrderId: string | undefined) => {
    if (!searchId.trim()) {
      setProductDetails([]);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      let url = `/api/v1/SalesEntry/GetProductDetailsForSalesEntry?SearchId=${searchId}&ProductSearchBy=${productSearchBy}`;
      if (customeOrderId) {
        url += `&CustomOrderId=${customeOrderId}`;
      }
      response = await apiClient.get(url);
      if (response.status === 400) {
        setError((response as any).message || 'Bad request')
        return
      }
      const details = response.data || [];
      setProductDetails(details);
      return details;
    } catch (err: any) {

      // Handle different HTTP status codes
      if (err.response?.status === 400) {
        // For 400 Bad Request, extract the specific error message from the API
        const errorMessage = err.response?.data?.message ||
          err.response?.data?.error ||
          err.response?.data?.title ||
          'Invalid request parameters';
        setError(errorMessage);
      } else {
        // Handle other errors (404, 500, network errors, etc.)
        const errorMessage = err.response?.data?.message ||
          err.response?.data?.error ||
          err.response?.data?.title ||
          err.response?.statusText ||
          err.message ||
          `No product found for ${searchId}`;
        setError(errorMessage);
      }

      setProductDetails([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const clearProductDetails = useCallback(() => {
    setProductDetails([]);
    setError(null);
  }, []);

  return {
    productDetails,
    loading,
    error,
    getProductDetailsForSalesEntry,
    clearProductDetails
  };
};

// Types for Store Discount API
export interface StoreDiscountDetails {
  discountLimit: number;
  discountFreez: number;
}

export interface OtpSendRequest {
  Purpose: string;
  MobileNo: string;
}

export interface OtpSendResponse {
  status: number;
  message: string;
  data?: {
    otpId: string;
    expiryTime: string;
  };
}

export interface OtpVerifyRequest {
  id: string;
  otp: string;
  Purpose: string;
}

export interface OtpVerifyResponse {
  status: number;
  message: string;
  data?: {
    isVerified: boolean;
  };
}

// Custom hook for store discount details
export const useStoreDiscountDetails = () => {
  const [discountDetails, setDiscountDetails] = useState<StoreDiscountDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getStoreDiscountDetails = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/v1/SalesEntry/GetStoreDiscountDetails');
      if (response.data) {
        setDiscountDetails(response.data);
        return response.data;
      }
      return null;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to get store discount details';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    discountDetails,
    loading,
    error,
    getStoreDiscountDetails
  };
};

// Types for Reward Points API
export interface CustomerRewardPoints {
  customerId: string;
  availablePoints: number;
  pointsValue: number;
  maxRedeemablePoints: number;
  conversionRate: number;
}

// Custom hook for customer reward points
export const useCustomerRewardPoints = () => {
  const [rewardPoints, setRewardPoints] = useState<CustomerRewardPoints | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCustomerAvailableRewardPoints = useCallback(async (customerId: string) => {
    if (customerId === null || customerId === undefined || customerId === '') {
      setRewardPoints(null);
      setError('Customer Details is required');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/api/v1/Reward/GetCustomerAvailableRewardPoint?customerId=${customerId}`);

      if (response.data) {
        const rewardData: CustomerRewardPoints = {
          customerId: customerId,
          availablePoints: response.data || 0,
          pointsValue: response.data.pointsValue || 0,
          maxRedeemablePoints: response.data.maxRedeemablePoints || response.data.availablePoints || 0,
          conversionRate: response.data.conversionRate || 1
        };
        setRewardPoints(rewardData);
        return rewardData;
      }
      return null;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.title ||
        err.message ||
        'Failed to get customer reward points';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearRewardPoints = useCallback(() => {
    setRewardPoints(null);
    setError(null);
  }, []);

  return {
    rewardPoints,
    loading,
    error,
    getCustomerAvailableRewardPoints,
    clearRewardPoints
  };
};

// Types for Discount Voucher API
export interface DiscountVoucherDetails {
  id: number;
  voucherId?: string;
  voucherCode: string;
  discountType?: 'percentage' | 'fixed';
  discountValue?: number;
  discountAmount?: number;
  amount: number;
  minOrderAmount?: number;
  minInvoiceAmt?: number;
  maxDiscountAmount?: number;
  isValid: boolean;
  isActive?: boolean;
  isAllStore?: boolean;
  isConsumerOfferApplicable?: boolean;
  voucherType?: string;
  fromDate?: string;
  message?: string;
  discountVoucherKTTypeMappings?: Array<{
    voucherId: number;
    ktTypeId: number;
    ktType: string;
  }>;
}

export interface DiscountVoucherResponse {
  data: DiscountVoucherDetails;
  status: number;
  message: string;
}

// Custom hook for discount voucher validation
// export const useDiscountVoucherValidation = () => {
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [voucherDetails, setVoucherDetails] = useState<DiscountVoucherDetails | null>(null);

//   const validateDiscountVoucher = useCallback(async (voucherCode: string, orderAmount?: number): Promise<DiscountVoucherDetails | null> => {
//     if (!voucherCode.trim()) {
//       setError('Voucher code is required');
//       return null;
//     }

//     setLoading(true);
//     setError(null);

//     try {
//       const response = await apiClient.get(`/api/v1/DiscountVoucher/GetDiscountDetailsByVoucher?voucherCode=${encodeURIComponent(voucherCode.toUpperCase())}`);

//       // Handle the new API response structure
//       if (response && response.data && response.status === 200) {
//         const apiData = response.data;

//         // Check if voucher is active
//         if (!apiData.isActive) {
//           const errorMsg = "This voucher is not active or has expired";
//           setError(errorMsg);
//           return null;
//         }

//         // Transform API response to match our interface
//         const discountData: DiscountVoucherDetails = {
//           id: apiData.id,
//           voucherId: apiData.id.toString(),
//           voucherCode: voucherCode.toUpperCase(),
//           amount: apiData.amount,
//           discountAmount: apiData.amount,
//           minOrderAmount: apiData.minInvoiceAmt,
//           minInvoiceAmt: apiData.minInvoiceAmt,
//           isValid: true,
//           isActive: apiData.isActive,
//           isAllStore: apiData.isAllStore,
//           isConsumerOfferApplicable: apiData.isConsumerOfferApplicable,
//           voucherType: apiData.voucherType,
//           fromDate: apiData.fromDate,
//           discountVoucherKTTypeMappings: apiData.discountVoucherKTTypeMappings,
//           message: (response as any).message || 'Voucher applied successfully'
//         };

//         // Check minimum order amount if specified and orderAmount is provided
//         const minOrderAmount = discountData.minInvoiceAmt || 0;
//         if (orderAmount && minOrderAmount > 0 && orderAmount < minOrderAmount) {
//           const errorMsg = `Minimum order amount of ₹${minOrderAmount.toLocaleString()} required for this voucher`;
//           setError(errorMsg);
//           return {
//             ...discountData,
//             isValid: false,
//             message: errorMsg
//           };
//         }

//         // Clear any previous errors on successful validation
//         setError(null);
//         setVoucherDetails(discountData);
//         return discountData;
//       } else {
//         const errorMsg = (response as any)?.message || response?.data?.message || "Invalid or expired voucher code";
//         setError(errorMsg);
//         return null;
//       }
//     } catch (err: any) {
//       let errorMsg = "Failed to validate voucher code. Please try again.";

//       if (err.response?.status === 404) {
//         errorMsg = "Voucher code not found or invalid";
//       } else if (err.response?.status === 400) {
//         errorMsg = err.response?.data?.message || "Invalid voucher code format";
//       } else if (err.response?.data?.message) {
//         errorMsg = err.response.data.message;
//       } else if (err.response?.message) {
//         errorMsg = err.response.message;
//       }

//       setError(errorMsg);
//       return null;
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   const clearVoucherDetails = useCallback(() => {
//     setVoucherDetails(null);
//     setError(null);
//   }, []);

//   return {
//     loading,
//     error,
//     voucherDetails,
//     validateDiscountVoucher,
//     clearVoucherDetails
//   };
// };



export const useDiscountVoucherValidation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateDiscountVoucher = async (voucherCode: string, metalKtId: (string | number)[], customerId: any) => {
    setLoading(true);
    try {
      const response = await apiClient.post('/api/v1/DiscountVoucher/GetDiscountDetailsByVoucher', {
        voucherCode,
        MetalKtId: metalKtId,
        customerId: customerId
      });

      if (response.status == 200) {
        return response.data; // e.g., { isValid: true, amount: 100, id: 5 }
      }
      else {
        notistack.notifyFromApi(response)
        return
      }

    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to validate');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { validateDiscountVoucher, loading, error };
};




// Custom hook for OTP operations
export const useOtp = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpId, setOtpId] = useState<string>('');
  const [expiryTime, setExpiryTime] = useState<string>('');

  const sendOtp = useCallback(async (mobileNo: string, purpose: string = 'Discount'): Promise<OtpSendResponse | null> => {
    setLoading(true);
    setError(null);

    try {
      const requestData: OtpSendRequest = {
        Purpose: purpose,
        MobileNo: mobileNo
      };

      const response = await apiClient.post('/api/v1/otp/SendOtp', requestData);

      if (response.status === 200) {
        // Store the actual otpId from API response
        const actualOtpId = (response as any).data?.toString() || (response as any).data;
        setOtpId(actualOtpId);
        setExpiryTime(new Date(Date.now() + 5 * 60 * 1000).toISOString()); // 5 minutes from now

        return {
          status: response.status,
          message: (response as any).message || 'OTP sent successfully',
          data: {
            otpId: actualOtpId,
            expiryTime: new Date(Date.now() + 5 * 60 * 1000).toISOString()
          }
        };
      }

      throw new Error((response as any).message || 'Failed to send OTP');
    } catch (err: any) {
      let errorMsg = 'Failed to send OTP';

      if (err.response?.status === 400) {
        errorMsg = err.response?.data?.message || 'Invalid mobile number or purpose';
      } else if (err.response?.status === 429) {
        errorMsg = 'Too many OTP requests. Please try again later.';
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }

      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtp = useCallback(async (otp: string, purpose: string = 'Discount'): Promise<OtpVerifyResponse | null> => {
    setLoading(true);
    setError(null);

    try {
      const requestData: OtpVerifyRequest = {
        id: otpId, // Use the actual otpId from SendOtp response
        otp: otp,
        Purpose: purpose
      };

      const response = await apiClient.post('/api/v1/otp/VerifyOtp', requestData);

      if (response.status === 200) {
        return {
          status: response.status,
          message: (response as any).message || 'OTP verified successfully',
          data: {
            isVerified: true
          }
        };
      }

      throw new Error((response as any).message || 'Failed to verify OTP');
    } catch (err: any) {
      let errorMsg = 'Failed to verify OTP';

      if (err.response?.status === 400) {
        errorMsg = err.response?.data?.message || 'Invalid OTP';
      } else if (err.response?.status === 404) {
        errorMsg = 'OTP expired or not found';
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }

      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, [otpId]);

  const resetOtp = useCallback(() => {
    setOtpId('');
    setExpiryTime('');
    setError(null);
  }, []);

  return {
    loading,
    error,
    otpId,
    expiryTime,
    sendOtp,
    verifyOtp,
    resetOtp
  };
};

// Types for Karat API
export interface KaratSelectItem {
  id: number;
  value: string;
  text: string;
  selected: boolean;
  disabled: boolean;
}

// Custom hook for Karat select list
export const useKaratSelectList = () => {
  const [karatList, setKaratList] = useState<KaratSelectItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getKaratSelectList = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/v1/Karat/GetKaratSelectList');
      const rawData = response.data || [];

      // Transform data to ensure consistent structure and set 24KT as selected by default
      const transformedData = rawData.map((item: any) => ({
        id: item.id || item.value,
        value: item.value || item.text,
        text: item.text || item.value,
        selected: item.text === "24KT" ? true : (item.selected || false),
        disabled: item.disabled || false,
        karatId: item.value
      }));

      setKaratList(transformedData);
      return transformedData;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to get karat select list';
      setError(errorMessage);
      setKaratList([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load karat list on mount
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (mounted) {
        await getKaratSelectList();
      }
    };

    load();

    return () => { mounted = false; };
  }, []); // Empty dependency array

  return {
    karatList,
    loading,
    error,
    getKaratSelectList
  };
};

// Types for Old Gold Rate API
export interface OldGoldRateResponse {
  karatId: number;
  metalId: number;
  rate: number;
  oldGoldRate: number;
  metalRate: number;
  purity: number;
  fromDate?: string;
  toDate?: string;
  deleted?: boolean;
  id?: number;
}

// Custom hook for Old Gold Rate by Karat ID
export const useOldGoldRate = () => {
  const [rateData, setRateData] = useState<OldGoldRateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getOldGoldRateByKaratId = useCallback(async (karatId: number): Promise<OldGoldRateResponse | null> => {
    if (!karatId) {
      setRateData(null);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/api/v1/GoldRateMaster/GetTodaysGoldRateMaster?ktId=${karatId}`);

      // Handle successful response with data
      // Check both possible response structures
      if (response.data && response.status === 200) {
        setLoading(false)
        // Try response.data.data first (nested structure), then response.data (direct structure)
        const apiData = response.data.data || response.data;

        const oldGoldRate = apiData.metalRate !== null && apiData.metalRate !== undefined ? Number(apiData.metalRate) : 0;
        const metalRate = apiData.metalRate !== null && apiData.metalRate !== undefined ? Number(apiData.metalRate) : 0;
        const purity = apiData.purity !== null && apiData.purity !== undefined ? Number(apiData.purity) : 0;

        const rateInfo: OldGoldRateResponse = {
          karatId: Number(apiData.karatId) || karatId,
          metalId: Number(apiData.metalId) || 0,
          rate: oldGoldRate, // Primary rate field should be oldGoldRate
          oldGoldRate: oldGoldRate,
          metalRate: metalRate,
          purity: purity,
          fromDate: apiData.fromDate,
          toDate: apiData.toDate,
          deleted: Boolean(apiData.deleted),
          id: Number(apiData.id) || 0
        };

        setRateData(rateInfo);
        return rateInfo;
      } else {
        setError('No valid old gold rate data found for the selected karat');
        setRateData(null);
        return null;
      }
    } catch (err: any) {
      let errorMessage = 'Failed to get old gold rate';

      // Handle specific 400 status with custom message
      if (err.response?.status === 400) {
        if (err.response?.data?.data) {
          // Handle case where error message is in data field
          errorMessage = err.response.data.data;
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else {
          errorMessage = 'No old gold rate setting found for the selected karat';
        }

        // For 400 status, set rate data with 0 rate instead of null
        const zeroRateInfo: OldGoldRateResponse = {
          karatId: karatId,
          metalId: 0,
          rate: 0,
          oldGoldRate: 0,
          metalRate: 0,
          purity: 0,
          fromDate: undefined,
          toDate: undefined,
          deleted: false,
          id: 0
        };

        setRateData(zeroRateInfo);
        setError(errorMessage);
        return zeroRateInfo;
      } else {
        errorMessage = err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to get old gold rate';

        setError(errorMessage);
        setRateData(null);
        return null;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const clearRateData = useCallback(() => {
    setRateData(null);
    setError(null);
  }, []);

  return {
    rateData,
    loading,
    error,
    getOldGoldRateByKaratId,
    clearRateData
  };
};

// Types for Exchange API
export interface ExchangeItem {
  id: string;
  salesOrderId: number;
  salesOrderItemId: number;
  invoiceNo: string;
  saleOrderDate: string;
  returnSalesOrderId: number;
  returnSaleOrderDate: string;
  categoryName: string;
  diaWt: number;
  netWt: number;
  diaQty: number;
  quantity: number;
  mrpPrice: number;
  finalSaleOrderPrice: number;
  materialTypeId: number;
  materialTypeName: string;
  metalAmount: number;
  laborAmount: number;
  otherCharges: number;
  discountAmount: number;
  totalSoAmount: number;
  finalSoAmount: number;
  returnAmount: number;
  finalDueAmount: number;
  materialDiscount: number;
  diamondDiscount: number;
  labDiscount: number;
  mrpDiscount: number;
  customerId: number;
  storeId: number;
  salesPersonId: number;
  cgst: number;
  sgst: number;
  subAmount: number;
  isOnGst: boolean;
  vocAmount: number;
  totalReturnSaleOrderAmount: number;
}

export interface ExchangeInvoice {
  invoiceNo: string;
  invoiceDate: string;
  customerName: string;
  customerMobile: string;
  totalAmount: number;
  totalItems: number;
  items: ExchangeItem[];
}

// Custom hook for exchange operations
export const useExchange = () => {
  const [exchangeInvoice, setExchangeInvoice] = useState<ExchangeInvoice | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchInvoiceForExchange = useCallback(async (invoiceNo: string): Promise<ExchangeInvoice | null> => {
    if (!invoiceNo.trim()) {
      setExchangeInvoice(null);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/api/v1/SalesEntry/GetSalesOrderForExchangeBySearch?invoiceNo=${encodeURIComponent(invoiceNo)}`);

      if (response.data) {
        const responseData = response.data || [];
        const firstItem = responseData[0];

        const invoiceData: ExchangeInvoice = {
          invoiceNo: firstItem?.invoiceNo || invoiceNo,
          invoiceDate: firstItem?.saleOrderDate || "",
          customerName: "", // This will need to be fetched separately or provided
          customerMobile: "", // This will need to be fetched separately or provided
          totalAmount: responseData.reduce((sum: number, item: any) => sum + (item.finalSaleOrderPrice || 0), 0),
          totalItems: responseData.length,
          items: responseData.map((item: any, index: number) => ({
            id: `${item.salesOrderId}-${item.salesOrderItemId}` || `item-${index}`,
            salesOrderId: Number(item.salesOrderId) || 0,
            salesOrderItemId: Number(item.salesOrderItemId) || 0,
            invoiceNo: item.invoiceNo || "",
            saleOrderDate: item.saleOrderDate || "",
            returnSalesOrderId: Number(item.returnSalesOrderId) || 0,
            returnSaleOrderDate: item.returnSaleOrderDate || "",
            categoryName: item.categoryName || "",
            diaWt: Number(item.diaWt) || 0,
            netWt: Number(item.netWt) || 0,
            diaQty: Number(item.diaQty) || 0,
            quantity: Number(item.quantity) || 0,
            mrpPrice: Number(item.mrpPrice) || 0,
            finalSaleOrderPrice: Number(item.finalSaleOrderPrice) || 0,
            materialTypeId: Number(item.materialTypeId) || 0,
            materialTypeName: item.materialTypeName || "",
            metalAmount: Number(item.metalAmount) || 0,
            laborAmount: Number(item.laborAmount) || 0,
            otherCharges: Number(item.otherCharges) || 0,
            discountAmount: Number(item.discountAmount) || 0,
            totalSoAmount: Number(item.totalSoAmount) || 0,
            finalSoAmount: Number(item.finalSoAmount) || 0,
            returnAmount: Number(item.returnAmount) || 0,
            finalDueAmount: Number(item.finalDueAmount) || 0,
            materialDiscount: Number(item.materialDiscount) || 0,
            diamondDiscount: Number(item.diamondDiscount) || 0,
            labDiscount: Number(item.labDiscount) || 0,
            mrpDiscount: Number(item.mrpDiscount) || 0,
            customerId: Number(item.customerId) || 0,
            storeId: Number(item.storeId) || 0,
            salesPersonId: Number(item.salesPersonId) || 0,
            cgst: Number(item.cgst) || 0,
            sgst: Number(item.sgst) || 0,
            subAmount: Number(item.subAmount) || 0,
            isOnGst: Boolean(item.isOnGst),
            vocAmount: Number(item.vocAmount) || 0,
          }))
        };

        setExchangeInvoice(invoiceData);
        return invoiceData;
      } else {
        const errorMsg = response.data?.message || "Invoice not found";
        setError(errorMsg);
        setExchangeInvoice(null);
        return null;
      }
    } catch (err: any) {
      let errorMessage = "Failed to search invoice";

      if (err.response?.status === 404) {
        errorMessage = "Invoice not found";
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || "Invalid invoice number format";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);
      setExchangeInvoice(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const validateExchangeItems = useCallback(async (invoiceNo: string, itemIds: string[]) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/v1/Exchange/ValidateExchangeItems', {
        invoiceNo,
        itemIds
      });
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to validate exchange items';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearExchangeData = useCallback(() => {
    setExchangeInvoice(null);
    setError(null);
  }, []);

  return {
    exchangeInvoice,
    loading,
    error,
    searchInvoiceForExchange,
    validateExchangeItems,
    clearExchangeData
  };
};

// Types for Customer Ledger API
export interface CustomerLedgerEntry {
  id: number;
  createdBy: number;
  deleted: boolean;
  narration: string;
  transactionType: 'Credit' | 'Debit';
  transactionAmount: number;
  billNo?: string;
  customerId: number;
  openingBalance: number;
  refundableBalance: number;
  refundAmount: number;
  transactionDate?: string;
  runningBalance?: number;
}

export interface CustomerLedgerResponse {
  data: CustomerLedgerEntry[];
  totalCount: number;
  groupCount: number;
}

// Custom hook for customer ledger operations
export const useCustomerLedger = () => {
  const [ledgerEntries, setLedgerEntries] = useState<CustomerLedgerEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalBalance, setTotalBalance] = useState({ credit: 0, debit: 0, balance: 0 });

  const getCustomerLedger = useCallback(async (customerId: number): Promise<CustomerLedgerEntry[]> => {
    if (!customerId) {
      setError('Customer ID is required');
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(`/api/v1/Ledger/GetCustomerLedgerDetailsByCustomerId?customerId=${customerId}`);
      if (response.data) {
        const ledgerData = response.data || [];

        // Calculate running balance and totals
        let runningBalance = 0;
        let totalCredit = 0;
        let totalDebit = 0;

        const processedEntries = ledgerData.map((entry: any) => {
          if (entry.transactionType === 'Credit') {
            runningBalance += entry.transactionAmount;
            totalCredit += entry.transactionAmount;
          } else {
            runningBalance -= entry.transactionAmount;
            totalDebit += entry.transactionAmount;
          }

          return {
            ...entry,
            runningBalance: runningBalance
          };
        });

        setLedgerEntries(processedEntries);
        setTotalBalance({
          credit: totalCredit,
          debit: totalDebit,
          balance: runningBalance
        });

        return processedEntries;
      } else {
        const errorMsg = response.data?.message || "Failed to fetch customer ledger";
        setError(errorMsg);
        return [];
      }
    } catch (err: any) {
      let errorMessage = "Failed to fetch customer ledger";

      if (err.response?.status === 404) {
        errorMessage = "Customer ledger not found";
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || "Invalid customer ID";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);
      setLedgerEntries([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const clearLedgerData = useCallback(() => {
    setLedgerEntries([]);
    setError(null);
    setTotalBalance({ credit: 0, debit: 0, balance: 0 });
  }, []);

  return {
    ledgerEntries,
    loading,
    error,
    totalBalance,
    getCustomerLedger,
    clearLedgerData
  };
};